import type { LucideIcon } from "lucide-react"
import type { ReactNode } from "react"

import { cn } from "@/lib/utils"

interface EmptyStateProps {
  icon?: LucideIcon
  title: string
  /**
   * Heading element for the title. Defaults to `"h1"` — correct for the
   * standalone/full-surface consumers (404 pages, invite-error pages,
   * dashboard cold-start replacements) where this is the page's primary
   * heading. Pass `"h2"`/`"h3"` when the EmptyState is EMBEDDED inside a
   * page that already owns an `<h1>` (e.g. the OKS-706 home-triage section
   * below the dashboard h1), so the single-h1 accessibility contract holds.
   */
  titleAs?: "h1" | "h2" | "h3"
  description?: string
  /**
   * Action slot — typically one or two `<Button>`s. Stacks on mobile,
   * lays out side-by-side on `sm:` and up.
   */
  actions?: ReactNode
  className?: string
  /**
   * ARIA landmark role applied to the container. Defaults to `"region"` —
   * the right fit for authenticated dashboard empty states that are part of
   * a larger navigation structure (sidebar + header).
   *
   * Pass `"status"` on unauthenticated / landing-style pages (e.g. the
   * invitation accept page's expired / accepted / revoked / mismatch /
   * rejected branches) so assistive tech announces the state on land.
   * UX Spec §3 calls this out explicitly.
   */
  role?: "region" | "status"
  /**
   * Override for the container's `data-testid`. Defaults to `"empty-state"`
   * so any consumer can target the shared primitive generically. Pages with
   * multiple empty surfaces (or QA needing a page-scoped hook) pass a
   * page-specific id — e.g. `"pending-invites-empty"` on Invitations/Index.
   *
   * OKS-122: the role-neutral testid surface on `/invitations` is documented
   * in `docs/frontend/invitations.md` — no role-prefixed aliases.
   *
   * OKS-114: the four admin list pages (Brands/Agencies/Creators/Blocklist)
   * pass `"admin-list-empty"` here to satisfy the Frontend v6 testid contract
   * for the admin list empty-state container (the inner clear-filters button
   * keeps its own `"admin-list-empty-clear"` hook).
   */
  "data-testid"?: string
}

/**
 * EmptyState — shared centered empty/error state primitive.
 *
 * Used by invite-error pages, role-scoped 404, the `/agency/brands`
 * OKS-13 F6 empty state, and any future "nothing here yet" surfaces.
 * Lives outside of any shell — keep it self-contained, no brand chrome,
 * no nav.
 *
 * OKS-562 (§23): the icon circle is a premium **peach** disc
 * (`bg-primary/10 text-primary`, the accent token at low alpha) rather than
 * the former grey `bg-muted`. This is a PRIMITIVE-level uplift, so every
 * consumer app-wide inherits the warmer treatment.
 */
export function EmptyState({
  icon: Icon,
  title,
  titleAs: TitleTag = "h1",
  description,
  actions,
  className,
  role = "region",
  "data-testid": dataTestId = "empty-state",
}: EmptyStateProps) {
  return (
    <div
      role={role}
      aria-label={title}
      className={cn(
        "mx-auto flex w-full max-w-md flex-col items-center justify-center gap-4 px-6 py-16 text-center",
        className,
      )}
      data-testid={dataTestId}
    >
      {Icon && (
        <div
          aria-hidden="true"
          className="bg-primary/10 text-primary flex size-14 items-center justify-center rounded-full"
        >
          <Icon className="size-7" />
        </div>
      )}
      {/*
        OKS-749 — keep the brand-anchored empty copy inside the content column so
        it never triggers horizontal scroll on the 375px creator home. Ordinary
        (breakable) copy already wraps within the column; the real trigger is an
        UNBREAKABLE long token — a long brand name/handle interpolated into the
        copy — whose min-content exceeds the column and pokes past the viewport
        edge (LQE-measured: scrollWidth 327 > 320 with only a box clamp). So the
        fix is three-fold: `w-full` gives a definite cross size under the parent's
        `items-center` (no stretch), `min-w-0` lets the box shrink in flow, and
        `wrap-anywhere` (`overflow-wrap: anywhere`) breaks the long token so it
        wraps in-column instead of overflowing. The container's `text-center`
        keeps everything centered — visually inert for short copy, corrective for
        long, across all EmptyState consumers.

        OKS-808 — regression guard for this exact clamp lives at
        `tests/playwright/helpers/empty-state-overflow.ts` (reusable across
        creator-home + a representative set of other consumers). Extend
        the representative set there, don't hand-roll a new inline check.
      */}
      <div className="w-full min-w-0 space-y-1.5 wrap-anywhere">
        <TitleTag className="text-foreground text-xl font-semibold tracking-tight">
          {title}
        </TitleTag>
        {description && (
          <p className="text-muted-foreground text-sm leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className="mt-2 flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:justify-center">
          {actions}
        </div>
      )}
    </div>
  )
}
